import { ReactNode } from "react";
import { useSelector } from "react-redux";
import { Navigate, useLocation } from "react-router-dom";

import { RootState } from "@/store";

interface PrivateRouteProps {
  children: ReactNode;
  requiredRoles?: Array<
    "admin" | "driver" | "employee" | "manager" | "partner" | "staff" | "user"
  >;
}

export default function PrivateRoute({
  children,
  requiredRoles,
}: PrivateRouteProps) {
  const { isAuthenticated, isLoading, user } = useSelector(
    (state: RootState) => state.auth,
  );
  const location = useLocation();

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <span className="loading loading-spinner loading-lg text-primary"></span>
          <p className="mt-4 text-base-content/70">Authenticating...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated || !user) {
    return <Navigate replace state={{ from: location }} to="/auth/login" />;
  }

  // If no specific roles are required, allow access (but still do basic role checks)
  if (!requiredRoles) {
    return <>{children}</>;
  }

  // Check if user has required role
  if (!requiredRoles.includes(user.role)) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="w-full max-w-md p-8 text-center">
          <div className="mb-4 text-6xl">🚫</div>
          <h1 className="mb-2 text-2xl font-bold text-error">Access Denied</h1>
          <p className="mb-6 text-base-content/70">
            You are not authorized to access this page. Please contact your
            administrator if you believe this is a mistake.
          </p>
          <a className="btn btn-primary" href="/auth/login">
            Back to Login
          </a>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
